/* eslint-disable react/display-name */
/* eslint-disable no-console */
import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import DataTable from 'react-data-table-component';
import notify from '../../../utils/notify';
import { api } from '../../../config';
import { SkeletonTable } from '../../../Components/Common/Skeleton';

// Two letters for the supplier avatar, skipping legal-form words like "PT".
const LEGAL_FORMS = /^(pt|cv|tbk|ltd|inc|co|corp|pte)\.?,?$/i;
const initialsOf = name => {
  const words = String(name || '')
    .split(/\s+/)
    .filter(word => /[a-z0-9]/i.test(word) && !LEGAL_FORMS.test(word));
  return (
    words
      .slice(0, 2)
      .map(word => word.replace(/[^a-z0-9]/gi, '').charAt(0))
      .join('')
      .toUpperCase() || '?'
  );
};

const DmutInvitationDataTable = forwardRef(
  (
    {
      procurementId,
      token,
      subStatusTitles = [],
      cargoCategories = [],
      onSelectionChange,
      selectedMitraList = [],
      procurementType,
      isProcurementCompleted,
    },
    ref
  ) => {
    const [data, setData] = useState({ items: [] });
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const initialSyncDone = useRef(false);
    const selectAllRef = useRef(null);

    useEffect(() => {
      const timer = window.setTimeout(() => setSearch(searchInput.trim()), 350);
      return () => window.clearTimeout(timer);
    }, [searchInput]);

    const syncDataDmut = useCallback(async () => {
      const url = `${api.API_URL}/dmut/SynchronizationDataDmut`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(
          `Sync Registered Supplier Data failed: ${response.status}`
        );
      }
      return response.json();
    }, [token]);

    const syncCompanyInfo = useCallback(async () => {
      const url = `${api.API_URL}/dmut/SynchronizeCompanyInfoData`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Sync Company Info failed: ${response.status}`);
      }
      return response.json();
    }, [token]);

    useImperativeHandle(ref, () => ({
      refresh: async () => {
        setLoading(true);
        try {
          await syncDataDmut();
          await syncCompanyInfo();
          await fetchData(currentPage, rowsPerPage);
        } catch (error) {
          console.error('Synch DMUT error:', error);
          notify.apiError(error, 'synchronize Registered Supplier data');
        } finally {
          setLoading(false);
        }
      },
    }));

    useEffect(() => {
      if (selectedMitraList.length > 0 && !initialSyncDone.current) {
        const ids = selectedMitraList.map(item => item.companyMitraId);
        setSelectedIds(ids);
        initialSyncDone.current = true;
      }
    }, [selectedMitraList]);

    useEffect(() => {
      if (procurementType !== 'PelelanganKhusus' || data.items.length === 0) {
        return;
      }
      const missing = data.items
        .map(row => row.id)
        .filter(id => !selectedIds.includes(id));
      if (missing.length === 0) {
        return;
      }
      const allIds = [...selectedIds, ...missing];
      setSelectedIds(allIds);
      onSelectionChange?.(allIds.map(id => ({ id: null, companyMitraId: id })));
      // onSelectionChange intentionally excluded: parent often passes a new
      // function reference each render, and including it here would re-fire
      // this effect after every call, causing an infinite update loop.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [procurementType, data.items, selectedIds]);

    const handleToggleDmut = (id, checked) => {
      if (procurementType === 'PelelanganKhusus') {
        return;
      }
      if (checked) {
        if (procurementType === 'TunjukLangsung' && selectedIds.length >= 1) {
          notify.warning(
            'Tunjuk Langsung allows only 1 Registered Supplier. Please select one.'
          );
          return;
        }
      } else {
        if (procurementType !== 'TunjukLangsung' && selectedIds.length <= 2) {
          notify.warning('Please select at least 2 Registered Suppliers.');
          return;
        }
      }
      setSelectedIds(prev => {
        const next = checked
          ? [...prev, id]
          : prev.filter(itemId => itemId !== id);
        onSelectionChange?.(
          next.map(itemId => ({ id: null, companyMitraId: itemId }))
        );
        return next;
      });
    };

    const handleSelectAll = e => {
      if (procurementType === 'PelelanganKhusus') {
        return;
      }
      if (procurementType === 'TunjukLangsung') {
        notify.warning(
          'Tunjuk Langsung allows only 1 Registered Supplier. Please select one.'
        );
        return;
      }
      if (e.target.checked && data.items.length < 2) {
        notify.warning(
          'At least 2 Registered Suppliers must be available to select.'
        );
        return;
      }
      const pageIds = data.items.map(row => row.id);
      const next = e.target.checked
        ? [...selectedIds, ...pageIds.filter(id => !selectedIds.includes(id))]
        : selectedIds.filter(id => !pageIds.includes(id));
      setSelectedIds(next);
      onSelectionChange?.(next.map(id => ({ id: null, companyMitraId: id })));
    };

    useEffect(() => {
      if (selectAllRef.current) {
        const onPage = data.items.filter(row =>
          selectedIds.includes(row.id)
        ).length;
        const isIndeterminate = onPage > 0 && onPage < data.items.length;
        selectAllRef.current.indeterminate = isIndeterminate;
      }
    }, [selectedIds, data.items]);

    const selectionLocked =
      isProcurementCompleted !== false ||
      procurementType === 'PelelanganKhusus';

    const columns = [
      {
        name: (
          <input
            className='form-check-input'
            type='checkbox'
            aria-label='Select all suppliers on this page'
            checked={
              data.items.length > 0 &&
              data.items.every(row => selectedIds.includes(row.id))
            }
            ref={selectAllRef}
            onChange={handleSelectAll}
            disabled={selectionLocked}
          />
        ),
        selector: row => selectedIds.includes(row.id),
        width: '56px',
        center: true,
        cell: row => (
          <input
            className='form-check-input'
            type='checkbox'
            aria-label={`Select ${row.companyName || 'supplier'}`}
            checked={selectedIds.includes(row.id)}
            onChange={e => handleToggleDmut(row.id, e.target.checked)}
            disabled={selectionLocked}
          />
        ),
      },
      {
        name: 'Registered supplier',
        selector: row => row.companyName,
        sortable: true,
        grow: 3,
        wrap: true,
        cell: row => (
          <div className='pd-supplier'>
            <span className='pd-supplier__avatar' aria-hidden='true'>
              {initialsOf(row.companyName)}
            </span>
            <span className='pd-supplier__name'>{row.companyName || '-'}</span>
          </div>
        ),
      },
      {
        name: 'PIC',
        selector: row => row.pic,
        sortable: true,
        grow: 2,
        wrap: true,
        cell: row =>
          row.pic ? (
            <span className='pd-supplier__meta'>
              <i className='ri-user-line' aria-hidden='true'></i>
              {row.pic}
            </span>
          ) : (
            <span className='text-muted'>-</span>
          ),
      },
      {
        name: 'PIC email',
        selector: row => row.settlementEmail,
        sortable: true,
        grow: 2,
        wrap: true,
        cell: row =>
          row.settlementEmail ? (
            <span className='pd-supplier__meta'>
              <i className='ri-mail-line' aria-hidden='true'></i>
              {row.settlementEmail}
            </span>
          ) : (
            <span className='text-muted'>-</span>
          ),
      },
      {
        // Trading email dan settlement email (PIC email) sama-sama bisa dipakai registrasi Registered Supplier.
        name: 'Trading email',
        selector: row => row.tradingEmail,
        sortable: true,
        grow: 2,
        wrap: true,
        cell: row =>
          row.tradingEmail ? (
            <span className='pd-supplier__meta'>
              <i className='ri-mail-line' aria-hidden='true'></i>
              {row.tradingEmail}
            </span>
          ) : (
            <span className='text-muted'>-</span>
          ),
      },
    ];

    const fetchData = useCallback(
      async (page, size) => {
        if (!procurementId) return;
        setLoading(true);
        try {
          let url = `${api.API_URL}/procurement/get-dmut-for-invitation?ProcurementId=${procurementId}&PageNumber=${page}&PageSize=${size}`;

          if (subStatusTitles.length > 0) {
            url += subStatusTitles
              .map(title => `&SubStatus=${encodeURIComponent(title)}`)
              .join('');
          }
          if (search) {
            url += `&Search=${encodeURIComponent(search)}`;
          }
          if (cargoCategories.length > 0) {
            url += cargoCategories
              .map(cat => `&CargoCategory=${encodeURIComponent(cat)}`)
              .join('');
          }

          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            credentials: 'same-origin',
          });
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const result = await response.json();
          setData({
            items: result.data?.items || result.items || [],
          });
          setTotalRows(result.data?.totalItems || result.totalItems || 0);
        } catch (error) {
          console.error('Error fetching DMUT invitation data:', error);
        } finally {
          setLoading(false);
        }
      },
      [procurementId, token, subStatusTitles, cargoCategories, search]
    );

    useEffect(() => {
      setCurrentPage(1);
      fetchData(1, rowsPerPage);
    }, [fetchData, rowsPerPage]);

    const handlePageChange = useCallback(
      page => {
        setCurrentPage(page);
        fetchData(page, rowsPerPage);
      },
      [fetchData, rowsPerPage]
    );

    const handlePerRowsChange = useCallback(newPerPage => {
      setRowsPerPage(newPerPage);
      setCurrentPage(1);
    }, []);

    return (
      <>
        <div className='pd-inv-toolbar'>
          <div className='pd-inv-search'>
            <i className='ri-search-line' aria-hidden='true'></i>
            <input
              type='search'
              className='form-control form-control-sm'
              placeholder='Search supplier, PIC or email'
              aria-label='Search registered suppliers'
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                type='button'
                className='pd-inv-search__clear'
                aria-label='Clear search'
                onClick={() => setSearchInput('')}
              >
                <i className='ri-close-line' aria-hidden='true'></i>
              </button>
            )}
          </div>
          <span className='pd-inv-toolbar__count' aria-live='polite'>
            {loading
              ? 'Searching…'
              : `${totalRows} supplier${totalRows === 1 ? '' : 's'}${
                  search ? ` match "${search}"` : ''
                }`}
          </span>
        </div>
        <DataTable
          columns={columns}
          data={data.items}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          highlightOnHover
          responsive
          persistTableHead
          conditionalRowStyles={[
            {
              when: row => selectedIds.includes(row.id),
              style: {
                backgroundColor: 'var(--vz-primary-bg-subtle)',
                boxShadow: 'inset 3px 0 0 var(--vz-primary)',
              },
            },
          ]}
          progressPending={loading}
          progressComponent={
            <table className='table mb-0 w-100'>
              <tbody>
                <SkeletonTable inline rows={5} columns={4} />
              </tbody>
            </table>
          }
          paginationRowsPerPageOptions={[10, 15, 25, 50]}
          noDataComponent={
            <div className='pd-inv-empty'>
              <span className='pd-inv-empty__icon' aria-hidden='true'>
                <i className='ri-group-line'></i>
              </span>
              <strong>
                {search
                  ? `No supplier matches "${search}"`
                  : 'No registered suppliers match'}
              </strong>
              <p>
                {search
                  ? 'Check the spelling, or search by PIC name or email instead.'
                  : 'Change the sub status or cargo category above, or sync the latest data from DMUT.'}
              </p>
            </div>
          }
          customStyles={{
            table: {
              style: {
                tableLayout: 'auto',
                width: '100%',
                backgroundColor: 'transparent',
              },
            },
            headRow: {
              style: {
                minHeight: '40px',
                backgroundColor: 'var(--vz-light)',
                borderBottom: '1px solid var(--vz-border-color)',
              },
            },
            headCells: {
              style: {
                paddingLeft: '12px',
                paddingRight: '12px',
                fontSize: '11px',
                fontWeight: '600',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--vz-secondary-color)',
                whiteSpace: 'normal',
              },
            },
            rows: {
              style: {
                minHeight: '52px',
                fontSize: '13px',
                color: 'var(--vz-body-color)',
                backgroundColor: 'var(--vz-card-bg)',
                '&:not(:last-of-type)': {
                  borderBottom: '1px solid var(--vz-border-color)',
                },
              },
              highlightOnHoverStyle: {
                backgroundColor: 'var(--vz-light)',
                borderBottomColor: 'var(--vz-border-color)',
                outline: 'none',
              },
            },
            cells: {
              style: {
                paddingLeft: '12px',
                paddingRight: '12px',
                paddingTop: '8px',
                paddingBottom: '8px',
                wordBreak: 'break-word',
              },
            },
            pagination: {
              style: {
                color: 'var(--vz-secondary-color)',
                backgroundColor: 'var(--vz-card-bg)',
                borderTop: '1px solid var(--vz-border-color)',
              },
            },
          }}
        />
      </>
    );
  }
);

export default DmutInvitationDataTable;
